import React from "react";
import { normalizeStage, stageOrder, MetricCard } from "./appShared";

const fmt2 = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export const AdminSalesDashboard = ({ prospects, pricing }) => {
  const [mrrData, setMrrData] = React.useState(null);
  const newIntakes = prospects.filter((p) => normalizeStage(p.stage) === "NEW_INTAKE").length;
  const qualified = prospects.filter((p) => {
    const s = normalizeStage(p.stage);
    return s === "QUALIFIED" || s === "QUAL_CALL_BOOKED" || s === "QUALIFIED_TO_BUY";
  }).length;
  
  const assessmentsPurchased = prospects.filter((p) => {
    const s = normalizeStage(p.stage);
    return s === "ASSESSMENT_PURCHASED" || s === "ASSESSMENT_IN_PROGRESS" || 
           s === "ASSESSMENT_DELIVERED" || stageOrder[s] > stageOrder.ASSESSMENT_DELIVERED;
  }).length;
  
  const projectsActive = prospects.filter((p) => {
    const s = normalizeStage(p.stage);
    return s === "PROJECT_IN_DELIVERY";
  }).length;
  
  const retainersActive = prospects.filter((p) => normalizeStage(p.stage) === "RETAINER_ACTIVE").length;
  const atRisk = prospects.filter((p) => normalizeStage(p.stage) === "AT_RISK").length;

  // Fetch MRR breakdown from backend
  React.useEffect(() => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BACKEND}/api/prospects/mrr-breakdown`, {
      headers: { "x-tenant-id": "tenant_1", "x-user-id": "admin", "x-user-role": "admin" },
    })
      .then(r => r.json())
      .then(d => setMrrData(d))
      .catch(() => {});
  }, []);

  // Dynamic pricing from backend
  const assessmentPrice = pricing?.pricing?.assessment?.amount || 4999;
  const implementationPrice = pricing?.pricing?.implementation?.amount || 9999;
  const retainerPlans = pricing?.retainer_plans || [];
  const defaultAvgRetainer = retainerPlans.length > 0
    ? retainerPlans.reduce((s, p) => s + (p.monthly_price || 0), 0) / retainerPlans.length
    : 5995;

  // Calculate MRR: use each customer's selected plan if available, otherwise avg
  let monthlyRecurring = 0;
  let mrrLabel = "";
  if (mrrData?.retainers?.length) {
    const retainerValues = mrrData.retainers.map(r => r.monthly_value || defaultAvgRetainer);
    monthlyRecurring = retainerValues.reduce((a, b) => a + b, 0);
    const withPlan = mrrData.retainers.filter(r => r.monthly_value).length;
    const avgValue = monthlyRecurring / retainerValues.length;
    mrrLabel = `${retainerValues.length} retainer${retainerValues.length !== 1 ? 's' : ''} @ avg ${fmt2.format(avgValue)}`;
    if (withPlan < retainerValues.length) {
      mrrLabel += ` (${retainerValues.length - withPlan} using default)`;
    }
  } else {
    monthlyRecurring = retainersActive * defaultAvgRetainer;
    mrrLabel = `${retainersActive} retainer${retainersActive !== 1 ? 's' : ''} @ avg ${fmt2.format(defaultAvgRetainer)}`;
  }

  const revenueBooked = prospects.reduce((sum, p) => {
    let total = sum;
    const s = normalizeStage(p.stage);
    if (stageOrder[s] >= stageOrder.ASSESSMENT_PURCHASED) total += assessmentPrice;
    if (stageOrder[s] >= stageOrder.PROJECT_IN_DELIVERY) total += implementationPrice;
    return total;
  }, 0);

  const fmt = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard label="New Intakes" value={newIntakes} sub="Awaiting qualification" accent={newIntakes > 0} />
        <MetricCard label="Qualified Opportunities" value={qualified} sub="Ready for assessment" />
        <MetricCard label="Assessments Sold" value={assessmentsPurchased} sub={`${fmt.format(assessmentPrice)} each`} />
        <MetricCard label="Projects Active" value={projectsActive} sub="In delivery" />
      </div>
      
      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard label="Revenue Booked" value={fmt.format(revenueBooked)} sub="One-time projects" accent />
        <MetricCard label="Active MRR" value={fmt.format(monthlyRecurring)} sub={mrrLabel} />
        <MetricCard label="At Risk" value={atRisk} sub="Need attention" accent={atRisk > 0} />
      </div>
    </div>
  );
};
